import { useNavigate, useLocation } from "react-router";
import type { FeedbackReviewRow } from "../../types";
import { buildFeedbackDetailPath, feedbackItemKey } from "../../utils/feedbackItemKey";
import { formatCitationSummary } from "../../utils/formatCitationSummary";
import { AnnotationStatusBadge } from "../AnnotationStatusBadge/AnnotationStatusBadge";
import { FeedbackValueBadge } from "../FeedbackValueBadge/FeedbackValueBadge";
import styles from "./FeedbackReviewTable.module.css";

export interface FeedbackReviewTableProps {
  rows: FeedbackReviewRow[];
  isLoading?: boolean;
}

function TableSkeleton() {
  return (
    <tbody data-testid="feedback-table-skeleton">
      {Array.from({ length: 5 }, (_, index) => (
        <tr key={index}>
          {Array.from({ length: 6 }, (__, cellIndex) => (
            <td key={cellIndex} className={styles.td}>
              <div className={styles.skeletonCell} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function FeedbackRow({
  row,
  onSelect,
}: {
  row: FeedbackReviewRow;
  onSelect: (row: FeedbackReviewRow) => void;
}) {
  return (
    <tr
      className={styles.row}
      onClick={() => onSelect(row)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(row);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Review feedback: ${row.question_preview}`}
      data-testid={`feedback-row-${feedbackItemKey(row.item_ref)}`}
    >
      <td className={styles.td}>
        <span className={styles.questionPreview}>{row.question_preview}</span>
      </td>
      <td className={styles.td}>
        <span className={styles.answerPreview}>
          {row.answer_preview ?? "—"}
        </span>
      </td>
      <td className={styles.td}>{row.jurisdiction ?? "—"}</td>
      <td className={styles.td}>{formatCitationSummary(row.citations)}</td>
      <td className={styles.td}>
        <FeedbackValueBadge value={row.feedback_value} />
      </td>
      <td className={styles.td}>
        <AnnotationStatusBadge status={row.annotation_status} />
      </td>
    </tr>
  );
}

export function FeedbackReviewTable({
  rows,
  isLoading = false,
}: FeedbackReviewTableProps) {
  const navigate = useNavigate();
  const location = useLocation();

  function handleSelect(row: FeedbackReviewRow) {
    navigate({
      pathname: buildFeedbackDetailPath(row.item_ref),
      search: location.search,
    });
  }

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Question</th>
            <th className={styles.th}>Answer</th>
            <th className={styles.th}>Jurisdiction</th>
            <th className={styles.th}>Citations</th>
            <th className={styles.th}>Feedback</th>
            <th className={styles.th}>Annotation</th>
          </tr>
        </thead>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.td}>
                  <div className={styles.emptyRow} data-testid="table-empty">
                    No thumbs-up feedback found for the current filters.
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <FeedbackRow
                  key={feedbackItemKey(row.item_ref)}
                  row={row}
                  onSelect={handleSelect}
                />
              ))
            )}
          </tbody>
        )}
      </table>
    </div>
  );
}
